if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.allMedia = [];
        this.currentMediaIndex = 0;
        this.handleKeydown = null;

        this.pagerEl = null;
        this.pagerPrevBtn = null;
        this.pagerNextBtn = null;
        this.pagerText = null;
      }

      show(opener) {
        super.show(opener);

        this.setupMediaList();
        this.showActiveMedia();

        this.setupPager();

        this.handleKeydown = this.onKeyDown.bind(this);
        document.addEventListener('keydown', this.handleKeydown);
      }

      hide() {
        super.hide();

        this.removePager();

        if (this.handleKeydown) {
          document.removeEventListener('keydown', this.handleKeydown);
          this.handleKeydown = null;
        }
      }

      setupMediaList() {
        const content = this.querySelector('[role="document"]');
        this.allMedia = content ? Array.from(content.querySelectorAll('[data-media-id]')) : [];

        const openerEl = this.openedBy;
        const openerMediaId =
          openerEl?.getAttribute('data-media-id') ||
          openerEl?.dataset?.mediaId ||
          openerEl?.closest?.('[data-media-id]')?.getAttribute('data-media-id') ||
          null;

        const openerId = openerMediaId ? String(openerMediaId) : null;
        const openerNumeric = openerId ? openerId.match(/(\d+)\s*$/)?.[1] : null;

        const idx = this.allMedia.findIndex((media) => {
          const idRaw = media.getAttribute('data-media-id');
          if (!idRaw) return false;

          const id = String(idRaw);

          if (openerId && id === openerId) return true;

          if (openerNumeric) {
            if (id === openerNumeric) return true;
            if (id.endsWith(`-${openerNumeric}`)) return true;
            if (id.endsWith(`__${openerNumeric}`)) return true;
            if (id.endsWith(`_${openerNumeric}`)) return true;
          }
          return false;
        });

        this.currentMediaIndex = idx >= 0 ? idx : 0;
      }

      showActiveMedia() {
        if (!this.allMedia.length) return;

        // 只保留 active 一張（其他交給 CSS 隱藏）
        this.allMedia.forEach((media) => media.classList.remove('active'));

        const activeMedia = this.allMedia[this.currentMediaIndex];
        if (!activeMedia) return;

        activeMedia.classList.add('active');

        // 置中（可留可不留）
        const container = this.querySelector('[role="document"]');
        if (container) {
          const left =
            activeMedia.offsetLeft - (container.clientWidth - activeMedia.clientWidth) / 2;
          container.scrollTo({ left: Math.max(0, left), behavior: 'auto' });
        }

        // youtube deferred media
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }

        this.updatePager();
      }

      nextMedia() {
        if (this.currentMediaIndex < this.allMedia.length - 1) {
          this.currentMediaIndex += 1;
          this.showActiveMedia();
        }
      }

      prevMedia() {
        if (this.currentMediaIndex > 0) {
          this.currentMediaIndex -= 1;
          this.showActiveMedia();
        }
      }

      onKeyDown(event) {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.nextMedia();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.prevMedia();
        }
      }

      setupPager() {
        if (this.pagerEl) return;

        const dialog = this.querySelector('[role="dialog"]');
        if (!dialog) return;

        const pager = document.createElement('div');
        pager.className = 'product-modal__pager';
        pager.innerHTML = `
          <button type="button" class="product-modal__pager-btn product-modal__pager-btn--prev" aria-label="上一張">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <span class="product-modal__pager-text">1 / 1</span>
          <button type="button" class="product-modal__pager-btn product-modal__pager-btn--next" aria-label="下一張">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        `;

        dialog.appendChild(pager);

        this.pagerEl = pager;
        this.pagerPrevBtn = pager.querySelector('.product-modal__pager-btn--prev');
        this.pagerNextBtn = pager.querySelector('.product-modal__pager-btn--next');
        this.pagerText = pager.querySelector('.product-modal__pager-text');

        // ✅ 只擋 mouse pointerup（避免 iOS touch 被吃掉）
        [pager, this.pagerPrevBtn, this.pagerNextBtn].forEach((el) => {
          if (!el) return;
          el.addEventListener('pointerup', (e) => {
            if (e.pointerType === 'mouse') e.stopPropagation();
          });
        });

        this.pagerPrevBtn?.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.prevMedia();
        });

        this.pagerNextBtn?.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.nextMedia();
        });

        /* ✅ iPhone Safari：有時 click 會被吃掉，補 touchend */
        this.pagerPrevBtn?.addEventListener(
          'touchend',
          (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.prevMedia();
          },
          { passive: false }
        );

        this.pagerNextBtn?.addEventListener(
          'touchend',
          (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.nextMedia();
          },
          { passive: false }
        );

        this.updatePager();

      }

      removePager() {
        if (this.pagerEl) this.pagerEl.remove();
        this.pagerEl = null;
        this.pagerPrevBtn = null;
        this.pagerNextBtn = null;
        this.pagerText = null;
      }

      updatePager() {
        if (!this.pagerText) return;

        const total = this.allMedia.length || 1;
        const current = Math.min(this.currentMediaIndex + 1, total);

        this.pagerText.textContent = `${current} / ${total}`;

        if (this.pagerPrevBtn) this.pagerPrevBtn.disabled = this.currentMediaIndex <= 0;
        if (this.pagerNextBtn) this.pagerNextBtn.disabled = this.currentMediaIndex >= total - 1;
      }
    }
  );
}
